import React, {
  useEffect,
  useContext,
  useState,
  useRef,
  Fragment,
} from "react";
import { useHistory } from "react-router-dom";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  formatPrice,
  getSystemSettings,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import Moment from "react-moment";
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

//pages & includes
import ReportSidebar from "./ReportSidebar";

//context consumer
import { SettingsContext } from "../../../../contexts/Settings";
import { FoodContext } from "../../../../contexts/Food";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import CustomSpinLoader from "../../../components/CustomSpinLoader";

const optionOptions = [
  { label: "Stocks", value: "stocks" },
  { label: "Finished Goods", value: "finished_goods" },
];

const MultiBranchAnalysis = () => {
  const { t } = useTranslation();
  const history = useHistory();
  //getting context values here
  let { loading, setLoading, dataPaginating, generalSettings } =
    useContext(SettingsContext);
  const { foodForPOS } = useContext(FoodContext);
  const { ingredientItemForSearch } = useContext(RestaurantContext);

  //all data
  const [reportData, setReportData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const componentRef = useRef();

  const [date, setDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [option, setOption] = useState(optionOptions[1]);

  const [generatedReport, setGeneratedReport] = useState(false);
  const [siteName, setSiteName] = useState("");

  useEffect(() => {
    setSiteName(getSystemSettings(generalSettings, "siteName"));
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, [generalSettings]);

  const handleOption = (opt) => {
    setOption(opt);
    setSelectedItems([]);
  };

  const getReportData = () => {
    if (date !== null && option !== null) {
      setLoading(true);

      let formattedDate = moment(date).format("YYYY-MM-DD");
      let formattedToDate = moment(toDate).format("YYYY-MM-DD");

      const url = BASE_URL + "/settings/get-custom-stock-report";

      let payload = {
        option: option.value,
        date: formattedDate,
        to_date: formattedToDate,
        item_ids: selectedItems && selectedItems.length > 0 ? selectedItems.map((item) => item.id) : [],
      };

      return axios
        .post(url, payload, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        })
        .then((res) => {
          setReportData(res.data);
          setLoading(false);
          setGeneratedReport(true);

          if (res.data && res.data.length > 0) {
            setTimeout(() => {
              if (componentRef.current) {
                componentRef.current.handleDownload();
              }
            }, 500);
          } else {
            toast.info(`${_t(t("No data found"))}`, {
              position: "bottom-center",
              closeButton: false,
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              className: "text-center toast-notification",
            });
          }
        })
        .catch((error) => {
          setLoading(false);
          toast.error(`${_t(t("Please try again"))}`, {
            position: "bottom-center",
            closeButton: false,
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            className: "text-center toast-notification",
          });
        });
    } else {
      toast.error(`${_t(t("Please select date and option"))}`, {
        position: "bottom-center",
        closeButton: false,
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>{_t(t("Multi Branch Analysis"))}</title>
      </Helmet>

      <ReactHTMLTableToExcel
        id="test-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls"
        filename={`Multi Branch Analysis - ${option?.label || ""}`}
        sheet="sheet 1"
        buttonText="Download"
        ref={componentRef}
      />

      {/* Print Button Area */}
      <div className="d-none">
        <div className="fk-print">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                {reportData !== null &&
                  reportData !== undefined &&
                  reportData.length > 0 &&
                  generatedReport === true && (
                    <>
                      <div className="row gx-2 justify-content-center t-pb-15 t-pt-15 report">
                        <div className="col-12 t-mb-15 mb-md-0 table-responsive">
                          <table
                            className="table table-bordered table-hover min-table-height mt-3"
                            id="table-to-xls"
                          >
                            <thead className="align-middle">
                              <tr style={{ border: "0px" }}>
                                <th
                                  colSpan={
                                    Object.keys(reportData[0]).length + 1
                                  }
                                  style={{ border: "0px" }}
                                >
                                  <h3 className="text-center mb-0">
                                    {siteName}
                                  </h3>
                                  <h3 className="text-center mt-0">
                                    Multi Branch Analysis -{" "}
                                    {option?.label || ""}
                                  </h3>
                                  <p className="text-center">
                                    Date:&nbsp;
                                    <Moment format="LL">{date}</Moment>
                                  </p>
                                </th>
                              </tr>
                              <tr>
                                <th scope="col">{_t(t("S.no"))}</th>
                                {Object.keys(reportData[0]).map(
                                  (key, index) => (
                                    <th scope="col" key={index}>
                                      {_t(t(key))}
                                    </th>
                                  ),
                                )}
                              </tr>
                            </thead>
                            <tbody className="align-middle">
                              {reportData.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                  <td key="sno">{rowIndex + 1}</td>
                                  {Object.keys(row).map((key, colIndex) => {
                                    const val = row[key];
                                    let displayVal = val;

                                    // Format null as empty or 0, depending on needs.
                                    // In the provided sample, we can just show val or empty.
                                    if (val === null) {
                                      displayVal = "";
                                    } else if (typeof val === "number") {
                                      // If it's a numeric value and not a description, we could use formatPrice
                                      // Assuming Item Description is not numeric.
                                      // For now, if it is a number, we just show it.
                                      displayVal = val;
                                    }

                                    return <td key={colIndex}>{displayVal}</td>;
                                  })}
                                </tr>
                              ))}
                            </tbody>

                            <tfoot style={{ border: "0px" }}>
                              <tr style={{ border: "0px" }}>
                                <td
                                  colSpan={
                                    Object.keys(reportData[0]).length + 1
                                  }
                                  className="text-center"
                                  style={{ border: "0px" }}
                                >
                                  <h5 className="mt-3">Powered by indolj.pk</h5>
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-2 mb-lg-0">
              <div className="t-bg-white p-1">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    {/* next page data spin loading */}
                    <CustomSpinLoader loading={dataPaginating || loading} />
                    {/* spin loading ends */}

                    <>
                      <div key="smtp-form mt-2">
                        <div className="row mt-1 gx-2 align-items-center t-pt-2 t-pb-2">
                          <div className="col-12 t-mb-15 mb-md-0">
                            <ul className="t-list fk-breadcrumb">
                              <li className="fk-breadcrumb__list">
                                <span className="t-link fk-breadcrumb__link text-capitalize mt-2">
                                  {_t(t("Multi Branch Analysis"))}
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <div className="row mt-3">
                          <div className="col-2">
                            <DatePicker
                              selected={date}
                              onChange={(date) => setDate(date)}
                              peekNextMonth
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              className="form-control"
                              placeholderText={_t(t("Date"))}
                              shouldCloseOnSelect={true}
                            />
                          </div>
                          <div className="col-2">
                            <DatePicker
                              selected={toDate}
                              onChange={(date) => setToDate(date)}
                              peekNextMonth
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              className="form-control"
                              placeholderText={_t(t("To Date"))}
                              shouldCloseOnSelect={true}
                            />
                          </div>
                          <div className="col-2">
                            <Select
                              options={optionOptions}
                              isClearable={false}
                              value={option}
                              classNamePrefix="select"
                              className="table-text"
                              onChange={handleOption}
                              maxMenuHeight="200px"
                              placeholder={_t(t("Select Option")) + ".."}
                            />
                          </div>
                          <div className="col-3">
                            <Select
                              options={option && option.value === "finished_goods" ? (foodForPOS || []) : (ingredientItemForSearch || [])}
                              isClearable={true}
                              getOptionLabel={(option)=> option.name}
                              getOptionValue={(option)=> option.id}
                              isMulti={true}
                              value={selectedItems}
                              classNamePrefix="select"
                              className="table-text"
                              onChange={(val) => setSelectedItems(val)}
                              maxMenuHeight="200px"
                              placeholder={_t(t("Select Items")) + ".."}
                            />
                          </div>

                          <div className="col-3 d-flex justify-content-end align-items-center">
                            <button
                              className="btn btn-primary sm-text text-capitalize mr-2 px-3 py-2 rounded"
                              onClick={(e) => getReportData(e)}
                            >
                              GET REPORT
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default MultiBranchAnalysis;
